/**
 * A specimen can be recorded before its source cycle is known. The explicit
 * sentinel lets a patient opt out of a contextual cycle link without losing
 * the specimen record or silently attaching it to the current cycle.
 */
export const UNLINKED_SPECIMEN_CYCLE_ID = "__not_linked__";

export function resolveSpecimenCycleId({
  relatedCycleId,
  contextCycleId,
  contextCycleIsTheResult = false,
}: {
  relatedCycleId: string | null | undefined;
  contextCycleId: string | null | undefined;
  /**
   * True when this intake *is* the cycle's recorded result — the retrieval
   * outcome opened from the cycle's own step, rather than a Cryo Vault entry
   * that happens to have a cycle on screen behind it.
   *
   * Those eggs are that cycle's eggs; there is no coherent answer where they
   * belong to no cycle. Without this, the unlinked option won even here, the
   * specimen row was written with no `cycleId` at all, and the cycle then
   * refused to close: it reported four frozen eggs and nothing attached, with no
   * affordance able to reconcile the two.
   *
   * Left off, this function behaves exactly as before, which is what the generic
   * Cryo Vault path needs — preservation-only intake is a real thing to record.
   */
  contextCycleIsTheResult?: boolean;
}): string | undefined {
  if (contextCycleIsTheResult && contextCycleId) return contextCycleId;
  if (relatedCycleId === UNLINKED_SPECIMEN_CYCLE_ID) return undefined;
  return relatedCycleId ?? contextCycleId ?? undefined;
}

export function shouldCreateSpecimenCycle({
  selectedCycleId,
  relatedCycleId,
}: {
  selectedCycleId: string | undefined;
  relatedCycleId: string | null | undefined;
}): boolean {
  return (
    selectedCycleId === undefined &&
    relatedCycleId !== null &&
    relatedCycleId !== undefined &&
    relatedCycleId !== UNLINKED_SPECIMEN_CYCLE_ID
  );
}

/**
 * Whether the intake asks which cycle the specimen came from.
 *
 * It does not, when the intake *is* a cycle's recorded result: that answer is
 * already given by the cycle she opened it from, and any other answer either
 * contradicts it or — for a freshly described draft cycle — takes the result
 * away from her cycle entirely, leaving it reporting a yield with nothing
 * linked. Hiding it only while the source was unset was not enough: answering
 * the source revealed the question again.
 *
 * The generic Cryo Vault intake keeps asking, which is the whole point of that
 * surface.
 */
export function offersRelatedCycleQuestion({
  isCycleResultIntake,
  source,
}: {
  isCycleResultIntake: boolean;
  /** Whose material this is, as the Details step recorded it. */
  source: string | null | undefined;
}): boolean {
  if (isCycleResultIntake) return false;
  return source === "mine" || source === "partner";
}
